/**
 * Iter XX.142 — Activation funnel tracker.
 *
 * Fires named funnel events to BOTH Clarity (session-replay) and the
 * backend `activation_funnel_events` collection (aggregation source
 * of truth). Every event is deduped per session so a rapid re-render
 * doesn't double-count.
 *
 * The named steps must match `/app/backend/activation_funnel.py`
 * `FUNNEL_EVENTS` exactly.
 */
import { track as clarityTrack } from "./clarity";
import api from "./api";

const DEDUPE_KEY_PREFIX = "g_activation_funnel_v1__";

export const FUNNEL_EVENTS = [
  "landing_view",
  "entry_chip_tapped",
  "primary_cta_clicked",
  "conversation_started",
  "first_gully_reply_painted",
  "second_user_message_sent",
  "first_coaching_moment",
  "completed_arc",
  "my_cricket_preview_seen",
  "personalized_value_reveal_shown",
  "plus_seen",
  "account_created",
  "subscription_started",
  // Iter XX.270 — "Build Your Gully" narrative (Phase 1). Additive,
  // visibility-only. Guarded upstream by `gully_narrative_enabled`
  // feature flag so OFF cohort emits zero of these events.
  "gully_learned_something",
  "gully_taking_shape_seen",
  "gully_narrative_paywall_seen",
  "gully_knows_line_seen",
  // Iter XX.272 — Gully Challenge #001. Additive, visibility-only.
  // Guarded upstream by `gully_challenge_v1_enabled` feature flag +
  // challenge doc `enabled=True` — flag OFF cohort emits zero of
  // these events.
  "challenge_impression",
  "challenge_detail_view",
  "challenge_join_tap",
  "challenge_plus_paywall_view",
  "challenge_plus_purchase_success",
  "challenge_joined",
  "challenge_focus_created",
  "challenge_activity_completed",
  "challenge_progress_share",
  "challenge_completed",
  // Iter XX.273 — Continue This Journey conversion surface.
  // Additive visibility + intent events on the existing arc-close
  // 402 branch. Purchase attribution is correlated via
  // `journey_origin` stash → canonical `subscription_started`.
  "journey_offer_impression",
  "journey_offer_tap",
  "journey_offer_paywall_view",
  "journey_offer_purchase_success",
  // Iter XX.274 — Gully Pro Founding 100 · web-only pre-sell.
  // `founding100_purchase_success` is emitted SERVER-SIDE inside
  // the Stripe-signature-verified webhook — mirrored here only
  // because the allow-list is checked in both directions.
  "founding100_page_view",
  "founding100_checkout_started",
  "founding100_purchase_success",
];

function _dedupedFire(event, payload = {}) {
  try {
    const k = `${DEDUPE_KEY_PREFIX}${event}`;
    if (sessionStorage.getItem(k) === "1") return false;
    sessionStorage.setItem(k, "1");
  } catch { /* sessionStorage disabled — proceed anyway */ }
  try { clarityTrack(event, payload); } catch { /* noop */ }
  // Post to backend — never block on network. Fail-open.
  try {
    api.post("/activation-funnel/track", {
      event,
      session_id: payload.session_id
        || (typeof localStorage !== "undefined"
              ? localStorage.getItem("gully_web_session_id")
              : null),
      landing_page_id: payload.landing_page_id || null,
      audience: payload.audience || null,
      payload,
    }).catch(() => { /* funnel is fire-and-forget */ });
  } catch { /* noop */ }
  return true;
}

/**
 * Fire one funnel event exactly once per session, with optional
 * payload merged into both Clarity + backend records.
 *
 * @param {string} event  — one of FUNNEL_EVENTS
 * @param {object} [payload]
 * @returns {boolean} — whether the event actually fired (false if deduped)
 */
export function trackFunnel(event, payload = {}) {
  if (!FUNNEL_EVENTS.includes(event)) {
    // eslint-disable-next-line no-console
    console.warn("activation_funnel: unknown event", event);
    return false;
  }
  return _dedupedFire(event, payload);
}

/**
 * Debug reset — clears session dedupe so a founder can re-trigger
 * the funnel in the same tab.
 */
export function resetFunnelDedupe() {
  try {
    FUNNEL_EVENTS.forEach((e) => sessionStorage.removeItem(`${DEDUPE_KEY_PREFIX}${e}`));
  } catch { /* noop */ }
}
